import type { ReactNode } from "react";
import { ChevronRight, Loader2 } from "lucide-react";

import SmartTableStalenessIndicator from "../../../SmartTableStalenessIndicator";

import ScorecardRunStatusCounts, {
  type ScorecardRunStatusCountsData,
} from "./ScorecardRunStatusCounts";
import ScorecardScoreSummary from "./ScorecardView/Detail/ScorecardScoreSummary";

import {
  OverflowDirection,
  OverflowDisplayTooltip,
} from "@/components/ui/Tooltip/OverflowDisplayTooltip";

/**
 * View-model for the overview header card. The card is the single surface for
 * "how is this scorecard doing right now": it renders the latest score when
 * idle and morphs into live run progress while a calculation is active,
 * instead of stacking a separate running banner above the score card.
 */
export type ScorecardHeaderCardState =
  | {
      kind: "score";
      score: ReactNode;
      drift?: number | null;
      verdictLabel?: ReactNode;
      verdictClassName?: string;
      metaLabel?: string | null;
      isStale?: boolean;
    }
  | {
      kind: "running";
      score: ReactNode;
      /** Scope-aware pill label, e.g. "Running" / "Running evaluator". */
      statusLabel: string;
      metaLabel?: string | null;
      progress?: {
        scoredRows: number;
        totalRows: number;
        statusCounts?: ScorecardRunStatusCountsData | null;
      } | null;
    };

interface ScorecardHeaderCardProps {
  state: ScorecardHeaderCardState;
  onOpenAllRows: () => void;
  /** Right-aligned action: re-run button when idle, Stop while running. */
  action?: ReactNode;
}

const RUNNING_PILL_CLASS = "border-blue-200 bg-blue-50 text-blue-700";

const renderScoreTooltipContent = (value: string) => (
  <div className="max-h-80 max-w-[520px] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-5">
    {value}
  </div>
);

const ScorecardHeaderCard = ({ state, onOpenAllRows, action }: ScorecardHeaderCardProps) => {
  const isRunning = state.kind === "running";
  const progress = isRunning ? state.progress : null;
  const progressPercent =
    progress && progress.totalRows > 0
      ? Math.min(100, (progress.scoredRows / progress.totalRows) * 100)
      : 0;
  const displayedScore =
    typeof state.score === "string" ? (
      <OverflowDisplayTooltip
        content={renderScoreTooltipContent(state.score)}
        overflowDirection={OverflowDirection.VERTICAL}
        lineClamp={2}
        side="top"
        className="max-w-full break-words"
        styles={{ wordBreak: "break-word" }}
      >
        <span>{state.score}</span>
      </OverflowDisplayTooltip>
    ) : (
      state.score
    );

  return (
    <div className="rounded-lg border border-border/70 bg-muted/10 px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={onOpenAllRows}
          className="group min-w-0 flex-1 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <ScorecardScoreSummary
            score={displayedScore}
            drift={isRunning ? undefined : state.drift}
            verdictLabel={
              isRunning ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  {state.statusLabel}
                </span>
              ) : (
                state.verdictLabel
              )
            }
            verdictClassName={isRunning ? RUNNING_PILL_CLASS : state.verdictClassName}
            trailingContent={
              !isRunning && state.isStale ? (
                <SmartTableStalenessIndicator
                  className="shrink-0"
                  tooltipContent="Stale — source data or config changed. Re-run to refresh."
                />
              ) : undefined
            }
            scoreClassName="min-w-0 max-w-full text-2xl leading-tight"
            containerClassName="min-w-0 flex-nowrap gap-x-2 gap-y-1"
          />
          {progress && progress.totalRows > 0 ? (
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-blue-100/80">
              <div
                className="h-full rounded-full bg-blue-500 transition-[width] duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          ) : null}
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {state.metaLabel ? <span className="tabular-nums">{state.metaLabel}</span> : null}
            <span className="inline-flex items-center gap-0.5 font-medium transition-colors group-hover:text-foreground">
              All rows
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
          {progress?.statusCounts ? (
            <ScorecardRunStatusCounts counts={progress.statusCounts} className="mt-1.5" />
          ) : null}
        </button>
        {action}
      </div>
    </div>
  );
};

export default ScorecardHeaderCard;
